/**
 * RelationshipCard - the "Lead" block on an Account / Site detail page (A-10;
 * it used to be titled Relationship): stage (+ time-in-stage and a read-only history
 * of stage, designation and status changes), Designation (A-01), Status
 * (A-03), owner, lead source + detail (A-05 / A-06), NDA lifecycle (a Site
 * inherits its parent's NDA unless the plant signs its own) and the sites list
 * (create progressively - an Account with zero Sites is a complete record).
 * Vertical, target score / tier, robot brands and Details moved to the
 * Account Details card (AccountDetailsCard.jsx) in r43, so this card holds
 * the lead journey only.
 *
 * Each row is <span>Label</span> + its control; _scratch/r42_leads_verify.js
 * finds the Stage, Lead source, Status and Designation controls that way.
 *
 * Paul's answers (9/28/26): a Customer never goes back to Lead (Q1); a company
 * with sites derives its stage and designation from them and has no Status of
 * its own (Q4); a new site needs a stage, a lead source (Q8) and a first
 * task (Q11). A new site starts on its company's vertical and can override it
 * on its Account Details card (X-07, 10/1/26 - this replaced Q9).
 *
 * onSave resolves to { ok, error } (AppContext.updateAccount). A refusal - the
 * Discovery Meeting Complete meeting gate, a Customer set back to Lead - is
 * shown in plain English under the row it came from. onCreateSite resolves the
 * same way, and the form stays open with the reason when it fails.
 *
 * Field names arrive camelCased from AppContext (stage_history -> stageHistory);
 * pickField reads either spelling in case a nested row was not converted.
 */
import { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import {
  CRM_STAGES,
  DEFAULT_STAGE,
  STAGE_COLORS,
  STAGE_DEFINITIONS,
  LEAD_SOURCES,
  NDA_STATUSES,
  LEAD_STATUSES,
  LEAD_STATUS_DEFINITIONS,
  LEAD_STATUS_COLORS,
  daysInStage,
  pickField,
  designationOf,
  leadStatusOf,
  MEETING_GATE_STAGE,
  DESIGNATIONS,
  DESIGNATION_DEFINITIONS,
} from '../../data/crmStages';
import { fmtDate } from '../../utils/format';
import { ERROR_LINE, tagStyle, designationTag, statusTag } from './crmStyles';
import { firstTaskErrors, taskPayload } from './crmFormHelpers';
import TagPeople from '../tasks/TagPeople';
import { NotTrackedNote } from './NotTrackedLead';
import NdaFiles from './NdaFiles';

const row = { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid #F3F4EF', flexWrap: 'wrap' };
const lbl = { fontSize: 10.5, color: '#777', fontWeight: 700, width: 130, flexShrink: 0 };
const sel = {
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #DDD',
  fontSize: 11,
  fontFamily: 'Montserrat',
  outline: 'none',
  background: '#fff',
};
const hint = { fontSize: 9.5, color: '#888' };
const underRow = { flexBasis: '100%', paddingLeft: 140, boxSizing: 'border-box' };
const btnSm = {
  padding: '3px 9px',
  borderRadius: 6,
  border: '1px solid #DDD',
  background: '#fff',
  color: '#666',
  fontSize: 9.5,
  fontWeight: 700,
  cursor: 'pointer',
  fontFamily: 'Montserrat',
};
const miniLbl = { fontSize: 9, color: '#999', fontWeight: 700, textTransform: 'uppercase' };

const blankSite = (me) => ({
  name: '',
  location: '',
  stage: DEFAULT_STAGE,
  lead_source: '',
  lead_source_detail: '',
  owner: me,
  task: { description: '', owner: me, due_date: '', tagged: [] },
});

export default function RelationshipCard({
  account,
  sites = [],
  editable = false,
  onSave,
  onOpenSite = () => {},
  onCreateSite = async () => ({ ok: true }),
}) {
  const { users } = useApp();
  const { user } = useAuth();
  const me = (user && user.name) || '';
  const [siteForm, setSiteForm] = useState(null); // blankSite()
  const [siteErr, setSiteErr] = useState('');
  const [siteBusy, setSiteBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [ndaOverride, setNdaOverride] = useState(false);
  const [detailDraft, setDetailDraft] = useState(null); // lead source detail, saved on blur
  const [saveErr, setSaveErr] = useState(null); // { row, msg } - the server's reason

  const days = daysInStage(account);
  const locked = !!account.stageLocked;
  const stage = account.stageEffective || account.stage || DEFAULT_STAGE;
  const isSite = !!account.parentAccountId;
  // a company with sites is not a lead itself - its sites are (stage, designation derived; no Status)
  const companyWithSites = !isSite && ((account.siteCount || 0) > 0 || sites.length > 0);
  const history = pickField(account, 'stageHistory', 'stage_history') || [];
  const statusHistory = pickField(account, 'statusHistory', 'status_history') || [];
  const historyCount = history.length + statusHistory.length;
  const needsReview = !!pickField(account, 'stageReview', 'stage_review');
  const inherited = pickField(account, 'ndaInherited', 'nda_inherited');
  const showInherited = isSite && !!inherited && !account.ndaStatus && !ndaOverride;
  const leadSource = account.leadSource || '';
  const sourceDetail = pickField(account, 'leadSourceDetail', 'lead_source_detail') || '';

  // Designation (A-01): stored vs effective (a won opportunity or a customer site makes it Customer)
  const storedDesig = account.designation || 'Lead';
  const effDesig = designationOf(account);
  const desigReason = pickField(account, 'designationReason', 'designation_reason');
  const desigLockedRaw = pickField(account, 'designationLocked', 'designation_locked');
  const desigLocked = desigLockedRaw != null ? !!desigLockedRaw : companyWithSites;
  const derivedNote =
    effDesig !== 'Customer'
      ? null
      : desigReason === 'won'
        ? `Customer - from a won opportunity${storedDesig === 'Account' ? ' (an Account until then; undoing the win puts it back)' : ''}`
        : desigReason === 'site'
          ? 'Customer - one of its sites is a customer'
          : null;
  const status = leadStatusOf(account);

  const ownerOptions = (extra) =>
    Array.from(new Set([...(users || []).map((u) => u.name).filter(Boolean), ...extra.filter(Boolean)])).sort((a, b) => a.localeCompare(b));
  const accountOwners = ownerOptions([account.owner]);

  const patch = async (fields, rowKey) => {
    setSaveErr(null);
    const r = await onSave(fields);
    if (r && r.ok === false) setSaveErr({ row: rowKey, msg: r.error || 'Could not save - try again.' });
    return r;
  };
  const errUnder = (rowKey) =>
    saveErr && saveErr.row === rowKey ? (
      <div style={underRow}>
        <div style={ERROR_LINE}>{saveErr.msg}</div>
      </div>
    ) : null;

  // keep the typed text on screen until the save (and refresh) is back, so it doesn't flicker to the old value
  const commitDetail = async () => {
    if (detailDraft === null) return;
    const v = detailDraft.trim();
    if (v !== sourceDetail) await patch({ lead_source_detail: v || null }, 'source');
    setDetailDraft(null);
  };
  const setDesignation = (next) => {
    if (next === storedDesig) return;
    if (
      next === 'Customer' &&
      !window.confirm(`Mark ${account.name} as a Customer? A Customer can't be set back to Lead or Account, and it leaves the Leads board.`)
    )
      return;
    patch({ designation: next }, 'designation');
  };

  const createSite = async () => {
    const f = siteForm;
    if (!f.name.trim()) {
      setSiteErr('Give the site a name.');
      return;
    }
    if (!f.stage) {
      setSiteErr('Pick a stage.');
      return;
    }
    if (!f.lead_source) {
      setSiteErr('Pick a lead source.');
      return;
    }
    const taskMsg = Object.values(firstTaskErrors(f.task))[0];
    if (taskMsg) {
      setSiteErr(taskMsg);
      return;
    }
    setSiteBusy(true);
    setSiteErr('');
    try {
      const r = await onCreateSite({
        name: f.name.trim(),
        location: f.location.trim(),
        stage: f.stage,
        lead_source: f.lead_source,
        lead_source_detail: f.lead_source_detail.trim(),
        owner: f.owner,
        first_task: taskPayload(f.task),
      });
      if (r && r.ok === false) {
        setSiteErr(r.error || 'Could not create the site - try again.');
        return;
      }
      setSiteForm(null);
    } finally {
      setSiteBusy(false);
    }
  };
  const setSite = (k, v) => {
    setSiteForm((p) => ({ ...p, [k]: v }));
    setSiteErr('');
  };
  const setSiteTask = (k, v) => {
    setSiteForm((p) => ({ ...p, task: { ...p.task, [k]: v } }));
    setSiteErr('');
  };

  return (
    <div style={{ background: '#fff', border: '1px solid #ECECEC', borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#1B5E20', letterSpacing: 1.4, textTransform: 'uppercase', marginBottom: 8 }}>
        Lead
      </div>

      {/* made automatically for an opportunity: not counted as a lead until tracked (10/12/26) */}
      <NotTrackedNote
        account={account}
        editable={editable}
        onTrack={() => onSave({ auto_created: false })}
        onMakeAccount={() => patch({ designation: 'Account' }, 'designation')}
      />

      {/* stage + time in stage + history */}
      <div style={row}>
        <span style={lbl}>Stage</span>
        {locked ? (
          <span style={{ fontSize: 11.5, fontWeight: 800, color: STAGE_COLORS[stage] || '#333' }}>
            {stage}
            <span style={{ marginLeft: 8, ...hint, color: '#999', fontWeight: 600 }}>derived from this account's sites (read-only)</span>
          </span>
        ) : editable ? (
          <select
            value={account.stage || ''}
            title={STAGE_DEFINITIONS[account.stage] || ''}
            onChange={(e) => patch({ stage: e.target.value, ...(needsReview ? { stage_review: false } : {}) }, 'stage')}
            style={sel}
          >
            {/* no stored stage: showing the default here made picking it a no-op, so nothing saved */}
            {!account.stage && (
              <option value="" disabled>
                Pick a stage...
              </option>
            )}
            {CRM_STAGES.map((s) => (
              <option key={s} value={s} title={STAGE_DEFINITIONS[s]}>
                {s}
              </option>
            ))}
            {account.stage && !CRM_STAGES.includes(account.stage) && <option value={account.stage}>{account.stage} (legacy)</option>}
          </select>
        ) : (
          <span style={{ fontSize: 11.5, fontWeight: 800, color: STAGE_COLORS[stage] || '#333' }}>{stage}</span>
        )}
        {/* L-02: parked here by the migration from Target / Contact attempted / blank. Picking a
            stage, or "Mark reviewed", clears it. */}
        {needsReview && (
          <span
            title="Moved to this stage by the Phase 1.2 migration from an old or blank stage - check it and pick the right one"
            style={tagStyle('#8A6D00', '#FFF8E1')}
          >
            Needs review
          </span>
        )}
        {needsReview && editable && !locked && (
          <button onClick={() => patch({ stage_review: false }, 'stage')} style={{ ...btnSm, padding: '2px 8px' }}>
            Mark reviewed
          </button>
        )}
        {days != null && (
          <span style={{ fontSize: 10, fontWeight: 700, color: days > 45 ? '#B71C1C' : '#888' }}>
            {days}d in stage{days > 45 ? ' · stalled?' : ''}
          </span>
        )}
        {historyCount > 0 && (
          <button onClick={() => setShowHistory(!showHistory)} style={{ ...btnSm, border: 'none', padding: 0, color: '#1565C0' }}>
            {showHistory ? 'hide history' : `history (${historyCount})`}
          </button>
        )}
        {!locked && STAGE_DEFINITIONS[stage] && <div style={{ ...underRow, ...hint }}>{STAGE_DEFINITIONS[stage]}</div>}
        {errUnder('stage')}
      </div>
      {showHistory && (
        <div style={{ padding: '4px 0 8px 140px', borderBottom: '1px solid #F3F4EF' }}>
          {history.length > 0 && statusHistory.length > 0 && <div style={{ ...miniLbl, marginBottom: 2 }}>Stage</div>}
          {history.map((h, i) => (
            <div key={`s${i}`} style={{ display: 'flex', gap: 12, fontSize: 10, color: '#666', padding: '2px 0' }}>
              <span style={{ fontWeight: 800, color: STAGE_COLORS[h.stage] || '#333', width: 200 }}>{h.stage}</span>
              <span style={{ width: 80 }}>{fmtDate(pickField(h, 'changedAt', 'changed_at'))}</span>
              <span style={{ color: '#999' }}>{pickField(h, 'changedBy', 'changed_by') || ''}</span>
            </div>
          ))}
          {statusHistory.length > 0 && (
            <>
              <div style={{ ...miniLbl, margin: '6px 0 2px' }}>Designation and status</div>
              {statusHistory.map((h, i) => {
                const field = h.field === 'lead_status' || h.field === 'leadStatus' ? 'Status' : 'Designation';
                return (
                  <div key={`d${i}`} style={{ display: 'flex', gap: 12, fontSize: 10, color: '#666', padding: '2px 0' }}>
                    <span style={{ width: 200 }}>
                      <b>{field}</b>: {pickField(h, 'fromValue', 'from_value') || '-'} → <b>{pickField(h, 'toValue', 'to_value') || '-'}</b>
                    </span>
                    <span style={{ width: 80 }}>{fmtDate(pickField(h, 'changedAt', 'changed_at'))}</span>
                    <span style={{ color: '#999' }}>{pickField(h, 'changedBy', 'changed_by') || ''}</span>
                  </div>
                );
              })}
            </>
          )}
          <div style={{ fontSize: 9, color: '#bbb', marginTop: 2 }}>written automatically on every change · not editable</div>
        </div>
      )}

      {/* Designation (A-01): Lead -> Customer by hand, never back; a win or a customer site derives it */}
      <div style={row}>
        <span style={lbl}>Designation</span>
        {desigLocked ? (
          <>
            <span style={designationTag(effDesig)}>{effDesig}</span>
            <span style={hint}>
              {derivedNote ||
                (effDesig === 'Customer'
                  ? 'Customer'
                  : effDesig === 'Account'
                    ? 'every site is an Account'
                    : effDesig === 'Target'
                      ? 'its sites are Targets or Accounts'
                      : 'none of its sites is a customer yet')}{' '}
              · derived from its sites (read-only)
            </span>
          </>
        ) : derivedNote ? (
          <>
            <span style={designationTag('Customer')}>Customer</span>
            <span style={hint}>{derivedNote} (read-only)</span>
          </>
        ) : storedDesig === 'Customer' ? (
          <>
            <span style={designationTag('Customer')}>Customer</span>
            <span style={hint}>a Customer can't go back to Target, Lead or Account</span>
          </>
        ) : editable ? (
          <>
            <select
              value={storedDesig}
              onChange={(e) => setDesignation(e.target.value)}
              style={sel}
              aria-label="Designation"
              title={DESIGNATION_DEFINITIONS[storedDesig] || ''}
            >
              {DESIGNATIONS.map((d) => (
                <option key={d} value={d} title={DESIGNATION_DEFINITIONS[d]}>
                  {d}
                </option>
              ))}
            </select>
            <span style={hint}>
              {storedDesig === 'Target'
                ? 'Target: to pursue, no good contact yet - off the Leads board and lead counts. Set it to Lead once someone good is engaged.'
                : storedDesig === 'Account'
                  ? 'Account: tracked, not a lead - off the Leads board and lead counts. A won opportunity makes it a Customer automatically.'
                  : 'Lead: contact made, no PO yet. A won opportunity makes it a Customer automatically.'}
            </span>
          </>
        ) : (
          <span style={designationTag(effDesig)}>{effDesig}</span>
        )}
        {errUnder('designation')}
      </div>

      {/* Status (A-03): site by site; a company with sites has none of its own (Paul Q4) */}
      <div style={row}>
        <span style={lbl}>Status</span>
        {companyWithSites ? (
          <span style={hint}>Status is set on each site</span>
        ) : editable ? (
          <>
            <select
              value={status}
              title={LEAD_STATUS_DEFINITIONS[status] || ''}
              onChange={(e) => patch({ lead_status: e.target.value }, 'status')}
              style={sel}
            >
              {LEAD_STATUSES.map((s) => (
                <option key={s} value={s} title={LEAD_STATUS_DEFINITIONS[s]}>
                  {s}
                </option>
              ))}
              {!LEAD_STATUSES.includes(status) && <option value={status}>{status} (legacy)</option>}
            </select>
            <span style={hint}>{LEAD_STATUS_DEFINITIONS[status] || ''}</span>
          </>
        ) : (
          <>
            <span style={statusTag(LEAD_STATUS_COLORS[status])}>{status}</span>
            <span style={hint}>{LEAD_STATUS_DEFINITIONS[status] || ''}</span>
          </>
        )}
        {errUnder('status')}
      </div>

      {/* owner */}
      <div style={row}>
        <span style={lbl}>Owner</span>
        {editable ? (
          <select
            value={account.owner || ''}
            onChange={(e) => patch({ owner: e.target.value || null }, 'owner')}
            style={{ ...sel, width: 200 }}
          >
            <option value="">-</option>
            {accountOwners.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        ) : (
          <span style={{ fontSize: 11.5 }}>{account.owner || '-'}</span>
        )}
        {errUnder('owner')}
      </div>

      {/* lead source (A-05, required - no blank) + detail (A-06). Lives on the site. */}
      <div style={row}>
        <span style={lbl}>Lead source</span>
        {companyWithSites ? (
          <>
            <span style={{ fontSize: 11.5 }}>
              {leadSource || '-'}
              {sourceDetail ? ` · ${sourceDetail}` : ''}
            </span>
            <span style={hint}>each site carries its own lead source</span>
          </>
        ) : editable ? (
          <>
            <select
              value={leadSource}
              onChange={(e) => {
                if (e.target.value) patch({ lead_source: e.target.value }, 'source');
              }}
              style={{ ...sel, ...(leadSource ? {} : { borderColor: '#E65100', color: '#E65100' }) }}
            >
              {!leadSource && (
                <option value="" disabled>
                  Pick one...
                </option>
              )}
              {LEAD_SOURCES.map((s) => (
                <option key={s} value={s} style={{ color: '#0D0D0D' }}>
                  {s}
                </option>
              ))}
              {leadSource && !LEAD_SOURCES.includes(leadSource) && <option value={leadSource}>{leadSource} (old value)</option>}
            </select>
            <span style={{ ...lbl, width: 'auto' }}>Detail</span>
            <input
              value={detailDraft !== null ? detailDraft : sourceDetail}
              placeholder="which ad, which show, who referred"
              onChange={(e) => setDetailDraft(e.target.value)}
              onBlur={commitDetail}
              onKeyDown={(e) => {
                if (e.key === 'Enter') e.target.blur();
              }}
              style={{ ...sel, flex: 1, minWidth: 180 }}
            />
          </>
        ) : (
          <span style={{ fontSize: 11.5 }}>
            {leadSource || '-'}
            {sourceDetail ? ` · ${sourceDetail}` : ''}
          </span>
        )}
        {errUnder('source')}
      </div>

      {/* NDA - a site shows its parent's until it signs its own. Reference only (A-08). */}
      <div style={row}>
        <span style={lbl}>NDA</span>
        {showInherited ? (
          <>
            <span style={{ fontSize: 11.5, fontWeight: 700 }}>{inherited.status}</span>
            <span style={hint}>
              inherited from {inherited.from}
              {inherited.executed ? ` · executed ${fmtDate(inherited.executed)}` : ''}
              {inherited.expires ? ` · expires ${fmtDate(inherited.expires)}` : ''}
            </span>
            {editable && (
              <button onClick={() => setNdaOverride(true)} style={btnSm}>
                This plant signs its own
              </button>
            )}
          </>
        ) : (
          <>
            {editable ? (
              <select value={account.ndaStatus || ''} onChange={(e) => patch({ nda_status: e.target.value || null }, 'nda')} style={sel}>
                <option value="">-</option>
                {NDA_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            ) : (
              <span style={{ fontSize: 11.5, fontWeight: 700 }}>{account.ndaStatus || '-'}</span>
            )}
            {['ndaSent', 'ndaExecuted', 'ndaExpires'].map((f, i) => (
              <span key={f} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={miniLbl}>{['sent', 'executed', 'expires'][i]}</span>
                {editable ? (
                  <input
                    type="date"
                    value={(account[f] || '').slice(0, 10)}
                    style={{ ...sel, padding: '4px 6px' }}
                    onChange={(e) =>
                      patch(
                        { [{ ndaSent: 'nda_sent', ndaExecuted: 'nda_executed', ndaExpires: 'nda_expires' }[f]]: e.target.value || null },
                        'nda'
                      )
                    }
                  />
                ) : (
                  <span style={{ fontSize: 10.5 }}>{fmtDate(account[f]) || '-'}</span>
                )}
              </span>
            ))}
            {isSite && inherited && !account.ndaStatus && editable && (
              <button onClick={() => setNdaOverride(false)} style={btnSm}>
                Use {inherited.from}'s NDA
              </button>
            )}
          </>
        )}
        {errUnder('nda')}
        {!showInherited && <NdaFiles account={account} editable={editable} />}
      </div>

      {/* sites: only for parent accounts */}
      {!isSite && (
        <div style={{ paddingTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 10.5, fontWeight: 800, color: '#7B1FA2', letterSpacing: 1, textTransform: 'uppercase' }}>
              Sites / facilities ({sites.length})
            </span>
            {editable && !siteForm && (
              <button
                onClick={() => {
                  setSiteForm(blankSite(me));
                  setSiteErr('');
                }}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: '1px solid #7B1FA2',
                  background: '#fff',
                  color: '#7B1FA2',
                  fontSize: 9.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                + Add site
              </button>
            )}
          </div>
          <div style={{ fontSize: 9.5, color: '#aaa', margin: '2px 0 6px' }}>
            Create a site the first time you reach a named contact at a plant - never build them out up front.
          </div>
          {siteForm && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 7,
                marginBottom: 10,
                padding: '10px 12px',
                border: '1px solid #EADCF3',
                borderRadius: 8,
                background: '#FCFAFE',
              }}
            >
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <input
                  autoFocus
                  placeholder="Site name (e.g. Caterpillar - Decatur IL)"
                  value={siteForm.name}
                  onChange={(e) => setSite('name', e.target.value)}
                  style={{ ...sel, flex: 1, minWidth: 220 }}
                />
                <input
                  placeholder="City, State"
                  value={siteForm.location}
                  onChange={(e) => setSite('location', e.target.value)}
                  style={{ ...sel, width: 160 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={miniLbl}>Stage</span>
                <select
                  value={siteForm.stage}
                  onChange={(e) => setSite('stage', e.target.value)}
                  style={sel}
                  title={STAGE_DEFINITIONS[siteForm.stage] || ''}
                >
                  {CRM_STAGES.filter((s) => s !== MEETING_GATE_STAGE).map((s) => (
                    <option key={s} value={s} title={STAGE_DEFINITIONS[s]}>
                      {s}
                    </option>
                  ))}
                </select>
                <span style={miniLbl}>Lead source</span>
                <select value={siteForm.lead_source} onChange={(e) => setSite('lead_source', e.target.value)} style={sel}>
                  <option value="" disabled>
                    Pick one...
                  </option>
                  {LEAD_SOURCES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <input
                  placeholder="Lead source detail (optional)"
                  value={siteForm.lead_source_detail}
                  onChange={(e) => setSite('lead_source_detail', e.target.value)}
                  style={{ ...sel, flex: 1, minWidth: 160 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={miniLbl}>Site owner</span>
                <select value={siteForm.owner} onChange={(e) => setSite('owner', e.target.value)} style={{ ...sel, width: 180 }}>
                  <option value="">-</option>
                  {ownerOptions([siteForm.owner]).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span style={hint}>The site starts on {account.name}&apos;s vertical; change it on the site if that plant differs.</span>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ ...miniLbl, color: '#7B1FA2' }}>First task</span>
                <input
                  placeholder="What happens next (e.g. Send capabilities deck to Hong)"
                  value={siteForm.task.description}
                  onChange={(e) => setSiteTask('description', e.target.value)}
                  style={{ ...sel, flex: 1, minWidth: 220 }}
                />
                <select
                  value={siteForm.task.owner}
                  onChange={(e) => setSiteTask('owner', e.target.value)}
                  style={{ ...sel, width: 170 }}
                  title="Task owner"
                >
                  <option value="">Owner...</option>
                  {ownerOptions([siteForm.task.owner]).map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <input
                  type="date"
                  value={siteForm.task.due_date}
                  onChange={(e) => setSiteTask('due_date', e.target.value)}
                  style={{ ...sel, padding: '5px 6px' }}
                  title="Due date"
                />
                <TagPeople
                  value={siteForm.task.tagged}
                  onChange={(v) => setSiteTask('tagged', v)}
                  owner={siteForm.task.owner}
                  people={ownerOptions([])}
                  label="Tag people on the first task"
                  style={{ flex: '1 1 220px' }}
                />
              </div>
              <div style={{ fontSize: 9.5, color: '#999' }}>
                Every new site needs a stage, a lead source and a first task, so no lead starts without a next step.
              </div>
              {siteErr && <div style={ERROR_LINE}>{siteErr}</div>}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={createSite}
                  disabled={siteBusy}
                  style={{
                    padding: '5px 12px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#7B1FA2',
                    color: '#fff',
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: siteBusy ? 'wait' : 'pointer',
                    opacity: siteBusy ? 0.6 : 1,
                  }}
                >
                  {siteBusy ? 'Creating...' : 'Create site'}
                </button>
                <button
                  onClick={() => {
                    setSiteForm(null);
                    setSiteErr('');
                  }}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: '1px solid #DDD',
                    background: '#fff',
                    color: '#666',
                    fontSize: 10,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {sites.map((s) => {
            const sd = designationOf(s);
            const ss = leadStatusOf(s);
            return (
              <div
                key={s.id}
                onClick={() => onOpenSite(s)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 10px',
                  border: '1px solid #F0EAF6',
                  borderRadius: 8,
                  marginBottom: 5,
                  cursor: 'pointer',
                  background: '#FCFAFE',
                  flexWrap: 'wrap',
                }}
              >
                <span style={{ flex: 1, fontSize: 11.5, fontWeight: 700, color: '#0D0D0D' }}>{s.name}</span>
                <span style={{ fontSize: 10, color: '#888' }}>{s.location || ''}</span>
                <span style={designationTag(sd)}>{sd}</span>
                {ss !== 'Active' && <span style={statusTag(LEAD_STATUS_COLORS[ss])}>{ss}</span>}
                <span style={{ fontSize: 9.5, fontWeight: 800, color: STAGE_COLORS[s.stage] || '#999' }}>{s.stage || DEFAULT_STAGE}</span>
                <span style={{ fontSize: 9.5, color: '#888' }}>{s.owner || ''}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
